import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { tomos } from '../data/constants';
import { Check, ChevronDown, Trophy, BookOpen, Target, Layers, Users, MapPin } from 'lucide-react';
export function ProductOverview() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const includes = ['Planificación semanal completa', '40 clases listas por tomo', 'Progresión de carga semana a semana', 'Benchmarks y tests con fichas de registro', 'Progresiones y regresiones por nivel', 'Cues técnicos listos para decir', 'Métodos explicados para el profe', 'Circuitos, AMRAP, For Time y EMOM listos', 'Aplicable con material estándar'];
  const valueBullets = [{
    icon: BookOpen,
    label: '300 páginas'
  }, {
    icon: Target,
    label: '240 clases completas'
  }, {
    icon: Layers,
    label: 'Sistema progresivo de 12 meses'
  }, {
    icon: Check,
    label: 'Listo para ejecutar'
  }, {
    icon: Users,
    label: 'Adaptable a grupos reales'
  }, {
    icon: MapPin,
    label: 'Creado para profes de Argentina'
  }];
  return <section className="py-16 md:py-24 px-4 max-w-5xl mx-auto" data-id="element-326">
      {/* SOLUTION INTRO */}
      <div className="text-center mb-12 md:mb-16" data-id="element-327">
        <p className="text-primary font-bold uppercase tracking-widest text-[11px] md:text-sm mb-3 flex items-center justify-center gap-2" data-id="element-328">
          <Trophy className="w-4 h-4" data-id="element-329" /> La solución
        </p>
        <h2 className="text-2xl md:text-5xl font-extrabold mb-4 leading-tight max-w-3xl mx-auto" data-id="element-330">
          El sistema completo para{' '}
          <span className="text-primary" data-id="element-331">resolver tu año de clases</span>
        </h2>
        <p className="text-base md:text-xl text-zinc-300 max-w-3xl mx-auto leading-relaxed" data-id="element-332">
          6 Tomos + 6 Bonos · 48 semanas · 240 clases listas · progresión anual
          real
        </p>
      </div>

      {/* VALUE BULLETS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-14 md:mb-16" data-id="element-333">
        {valueBullets.map(({
        icon: Icon,
        label
      }, i) => <div key={i} className="bg-surface border border-zinc-800 hover:border-primary/40 transition-colors rounded-xl p-4 md:p-5 flex items-center gap-3" data-id="element-334">
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0" data-id="element-335">
              <Icon className="w-4 h-4 text-primary" data-id="element-336" />
            </div>
            <span className="text-sm md:text-base font-bold text-white leading-tight" data-id="element-337">
              {label}
            </span>
          </div>)}
      </div>

      {/* 6 TOMOS */}
      <div className="mb-14 md:mb-16" data-id="element-338">
        <div className="text-center mb-8 md:mb-10" data-id="element-339">
          <h3 className="text-2xl md:text-4xl font-extrabold mb-3 leading-tight" data-id="element-340">
            6 tomos.{' '}
            <span className="text-primary" data-id="element-341">Una progresión anual completa.</span>
          </h3>
          <p className="text-sm md:text-base text-zinc-400" data-id="element-342">
            Tocá un tomo para ver el contenido
          </p>
        </div>

        <div className="space-y-3" data-id="element-343">
          {tomos.map((t, i) => {
          const isOpen = openIndex === i;
          return <div key={t.id} className={`rounded-2xl border overflow-hidden transition-colors duration-200 ${isOpen ? 'bg-surface border-primary shadow-[0_0_25px_rgba(74,222,128,0.15)]' : 'bg-surface border-zinc-800 hover:border-primary/40'}`} data-id="element-344">
                <button className="w-full px-5 py-4 md:px-7 md:py-5 text-left flex items-center justify-between gap-4 focus:outline-none" onClick={() => setOpenIndex(isOpen ? null : i)} data-id="element-345">
                  <div className="flex items-center gap-3 md:gap-4 min-w-0" data-id="element-346">
                    <span className="text-2xl md:text-3xl shrink-0" data-id="element-347">
                      {t.icon}
                    </span>
                    <div className="min-w-0" data-id="element-348">
                      <div className="font-bold text-white text-base md:text-lg leading-tight" data-id="element-349">
                        Tomo {i + 1} — {t.name}
                      </div>
                      <div className="text-xs md:text-sm text-primary font-medium mt-0.5" data-id="element-350">
                        {t.months}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-primary shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-351" />
                </button>

                <AnimatePresence data-id="element-352">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-353">
                      <div className="px-5 pb-5 md:px-7 md:pb-6 text-zinc-300 text-sm md:text-base leading-relaxed border-t border-primary/20 pt-4" data-id="element-354">
                        {t.content}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* WHAT EACH TOMO INCLUDES */}
      <div data-id="element-355">
        <div className="text-center mb-8 md:mb-10" data-id="element-356">
          <h3 className="text-2xl md:text-3xl font-extrabold leading-tight max-w-3xl mx-auto" data-id="element-357">
            Cada tomo viene listo para{' '}
            <span className="text-primary" data-id="element-358">entrar a clase y usar</span>
          </h3>
        </div>

        <div className="bg-surface border border-zinc-800 rounded-2xl p-6 md:p-8" data-id="element-359">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-id="element-360">
            {includes.map((item, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-361">
                <div className="mt-0.5 bg-primary/20 p-1 rounded-full shrink-0" data-id="element-362">
                  <Check className="w-3 h-3 text-primary" data-id="element-363" />
                </div>
                <span className="text-zinc-200 text-sm md:text-base" data-id="element-364">
                  {item}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}